import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const images = [
  "/image/images5.webp",
  "/image/images6.webp",
  "/image/images7.webp",
  "/image/images8.webp",
  "/image/images9.webp",
  "/image/images10.webp",
];

const Home = ({ language }: { language: string }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col min-h-screen overflow-hidden bg-gray-100">

      {/* Hero Section with Slideshow */}
      <section className="relative h-screen w-full">
        {/* Slideshow Background */}
        <div className="absolute inset-0 overflow-hidden">
          {images.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-in-out ${index === currentImageIndex ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/50 z-10" />
        </div>

        {/* Hero Content */}
        <div className="relative z-20 h-full flex flex-col items-center justify-center text-center px-4">
          <div className="max-w-4xl mx-auto space-y-5 animate-fade-in">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight">
              {language === "fr" ? "Bienvenue chez GMCL" : "Welcome to GMCL"}
            </h1>

            <h2 className="text-2xl md:text-3xl font-medium text-yellow-400">
              Garage Mécanique et Carrosserie Longueuil
            </h2>

            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              {language === "fr"
                ? "Des services mécaniques et de carrosserie exceptionnels pour votre véhicule. Expertise, qualité et transparence."
                : "Exceptional mechanical and bodywork services for your vehicle. Expertise, quality and transparency."}
            </p>

            {/* Services Buttons */}
            <div className="pt-2 space-y-6 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-yellow-400">
                {language === "fr" ? "Nos Services" : "Our Services"}
              </h3>

              <div className="flex justify-center">
                <div className="inline-flex flex-nowrap items-center gap-6 sm:gap-4 px-4 overflow-x-auto pb-2">
                  <Link
                    to="/services-mecanique"
                    className="flex-shrink-0 flex items-center gap-2 sm:gap-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:translate-y-[-2px] text-base sm:text-lg"
                  >
                    {language === "fr" ? "Mécanique" : "Mechanical"}
                  </Link>

                  <Link
                    to="/services-carrosserie"
                    className="flex-shrink-0 flex items-center gap-2 sm:gap-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:translate-y-[-2px] text-base sm:text-lg"
                  >
                    {language === "fr" ? "Carrosserie" : "Bodywork"}
                  </Link>
                </div>

              </div>
            </div>


          </div>

        </div>

        {/* Scroll Indicator */}
        <button
          onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 focus:outline-none mt-2" // mt-4 réduit la marge du haut
          aria-label={language === "fr" ? "Faire défiler vers le bas" : "Scroll down"}
        >
          <div className="animate-bounce">
            <svg className="w-8 h-8 text-yellow-400 hover:text-yellow-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </button>

      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-r from-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {language === "fr" ? "Contactez-nous" : "Contact Us"}
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Map */}
            <div className="rounded-xl overflow-hidden shadow-2xl transform transition duration-500 hover:scale-[1.02]">
              <iframe
                title="Google Maps Location"
                className="w-full h-96 border-0"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2794.356507048785!2d-73.48936548444454!3d45.54049157910152!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc922fb0571c18d%3A0x71e8c3fbb7e39f2c!2s2516%20Chem.%20du%20Lac%2C%20Longueuil%2C%20QC%20J4N%201G7%2C%20Canada!5e0!3m2!1sen!2sus!4v1614298023781!5m2!1sen!2sus"
                allowFullScreen
                loading="lazy"
              />
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-gray-50 p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  {language === "fr" ? "Adresse" : "Address"}
                </h3>
                <p className="text-lg text-gray-700">2516 Chem. du Lac, Longueuil, QC J4N 1G7</p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  {language === "fr" ? "Téléphone" : "Phone"}
                </h3>
                <a href="tel:+15147570004" className="text-lg text-yellow-600 hover:text-yellow-700 transition duration-300">
                  +1 514-757-0004
                </a>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12l-4-4-4 4m8 0l-4 4-4-4"></path>
                    <rect width="20" height="14" x="2" y="5" rx="2" ry="2"></rect>
                  </svg>
                  {language === "fr" ? "Email" : "Email"}
                </h3>
                <a href="mailto:gmcl.infos@gmail.com" className="text-lg text-yellow-600 hover:text-yellow-700 transition duration-300">
                  gmcl.infos@gmail.com
                </a>
              </div>



              <div className="bg-gray-50 p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  {language === "fr" ? "Heures d'ouverture" : "Opening Hours"}
                </h3>
                <ul className="space-y-3 text-gray-700">
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
                    <li key={day} className="flex justify-between">
                      <span>{language === "fr" ?
                        day === "Monday" ? "Lundi" :
                          day === "Tuesday" ? "Mardi" :
                            day === "Wednesday" ? "Mercredi" :
                              day === "Thursday" ? "Jeudi" :
                                day === "Friday" ? "Vendredi" :
                                  "Samedi" : day}</span>
                      <span className="font-medium">9 a.m. – 7 p.m.</span>
                    </li>
                  ))}
                  <li className="flex justify-between text-red-500">
                    <span>{language === "fr" ? "Dimanche" : "Sunday"}</span>
                    <span className="font-medium">{language === "fr" ? "Sur appel" : "By call"}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;